import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/client/lib/api-client'

export interface CrawlTrendPoint {
  crawlId: string
  date: string
  score: number | null
  pages: number
  issues: number
}

export interface RankingTrendPoint {
  date: string
  positions: Record<string, number | null> // keyword -> position
}

export interface RankingTrendSummary {
  averagePosition: number | null
  keywordsInTop3: number
  keywordsInTop10: number
  keywordsInTop20: number
}

export interface BacklinkTrendPoint {
  date: string
  totalBacklinks: number
  referringDomains: number
  domainRank: number | null
}

/** Crawl metrics over time (score, issues, pages) */
export function useCrawlTrends(siteId: string, days = 90) {
  return useQuery({
    queryKey: ['trends', 'crawls', siteId, days],
    queryFn: () => apiClient.get<{
      series: CrawlTrendPoint[]
    }>(`/api/sites/${siteId}/trends/crawls`, { params: { days } }),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  })
}

/** Ranking positions over time for all tracked keywords */
export function useRankingTrends(siteId: string, days = 90) {
  return useQuery({
    queryKey: ['trends', 'rankings', siteId, days],
    queryFn: () => apiClient.get<{
      keywords: string[]
      series: RankingTrendPoint[]
      summary: RankingTrendSummary
    }>(`/api/sites/${siteId}/trends/rankings`, { params: { days } }),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  })
}

export interface GscTrendPoint {
  snapshot_date: string
  total_clicks: number
  total_impressions: number
  avg_position: number
}

/** GSC weekly performance totals (from stored snapshots) */
export function useGscTrends(siteId: string, days = 180) {
  return useQuery({
    queryKey: ['trends', 'gsc', siteId, days],
    queryFn: () => apiClient.get<{
      series: GscTrendPoint[]
    }>(`/api/sites/${siteId}/trends/gsc`, { params: { days } }),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  })
}

export interface Ga4TrendPoint {
  snapshot_date: string
  sessions: number
  active_users: number
  page_views: number
  bounce_rate: number
  avg_session_duration: number
}

/** GA4 daily traffic trends (from stored snapshots) */
export function useGa4Trends(siteId: string, days = 180) {
  return useQuery({
    queryKey: ['trends', 'ga4', siteId, days],
    queryFn: () => apiClient.get<{
      trends: Ga4TrendPoint[]
    }>(`/api/sites/${siteId}/trends/ga4`, { params: { days } }),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  })
}

export interface CwvTrendPoint {
  crawl_id: string
  crawl_date: string
  avg_performance: number
  avg_lcp: number
  avg_cls: number
  avg_tbt: number
  pages_measured: number
}

/** Core Web Vitals trends across crawls (from Lighthouse data) */
export function useCwvTrends(siteId: string, days = 180) {
  return useQuery({
    queryKey: ['trends', 'cwv', siteId, days],
    queryFn: () => apiClient.get<{
      trends: CwvTrendPoint[]
    }>(`/api/sites/${siteId}/trends/cwv`, { params: { days } }),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  })
}

/** Backlink snapshot trends */
export function useBacklinkTrends(siteId: string, days = 180) {
  return useQuery({
    queryKey: ['trends', 'backlinks', siteId, days],
    queryFn: () => apiClient.get<{
      series: BacklinkTrendPoint[]
    }>(`/api/sites/${siteId}/trends/backlinks`, { params: { days } }),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  })
}
